import { createFileRoute, Link } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { btnPrimary, SkeletonRows } from '@/components/rm'
import { useT, type MessageKey } from '@/i18n'
import { fmtDate } from '@/lib/ph'
import { effectiveStatus, useMyTasks, type TaskStatus } from '@/lib/tasks'
import { cn } from '@/lib/utils'

// Spec 005. The client's tasks, newest first.
export const Route = createFileRoute('/_client/tasks')({ component: MyTasks })

export const STATUS_KEY: Record<TaskStatus, MessageKey> = {
  open: 'tasks.status.open', assigned: 'tasks.status.assigned', cancelled: 'tasks.status.cancelled', expired: 'tasks.status.expired',
}
export const STATUS_TONE: Record<TaskStatus, string> = {
  open: 'bg-token-info-surface text-token-info', assigned: 'bg-token-success-surface text-token-success',
  cancelled: 'bg-token-hover text-token-muted', expired: 'bg-token-hover text-token-muted',
}

function MyTasks() {
  const { t } = useT()
  const tasks = useMyTasks()
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{t('tasks.nav')}</h1>
        <Link to="/tasks/new" className={`${btnPrimary} h-10`}><Plus className="h-4 w-4" />{t('tasks.post')}</Link>
      </div>
      {tasks.isLoading && <SkeletonRows count={3} />}
      {!tasks.isLoading && (tasks.data ?? []).length === 0 && (
        <p className="rounded-[16px] border border-token-subtle bg-token-surface p-5 text-[14px] text-token-secondary">{t('tasks.empty')} {t('tasks.postHint')}</p>
      )}
      <ul className="space-y-3">
        {(tasks.data ?? []).map((task) => {
          const st = effectiveStatus(task)
          return (
            <li key={task.id}>
              <Link to="/tasks/$id" params={{ id: task.id }} className="block rounded-[16px] border border-token-subtle bg-token-surface p-4 hover:border-token-strong">
                <div className="flex items-start justify-between gap-3">
                  <p className="min-w-0 text-[15px] font-semibold text-token-primary">{task.title}</p>
                  <span className={cn('flex-none rounded-full px-2.5 py-0.5 text-[12px] font-semibold', STATUS_TONE[st])}>{t(STATUS_KEY[st])}</span>
                </div>
                <p className="mt-1 text-[12.5px] text-token-muted">{fmtDate(task.created_at)}</p>
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
